/* DoctorAvatar — small reusable doctor portrait with initials fallback.
 *
 * The doctor's profile photo is stored at `doctors.image` as a path like
 * `/api/public/doctor-avatar/{id}?v={hash}`, served by the public no-auth
 * proxy. We render that path directly (the frontend is served from the
 * same origin as the backend in our preview + prod envs, so relative
 * URLs resolve correctly).
 *
 * Falls back gracefully to initials in primary-tinted indigo when:
 *   - the doctor hasn't uploaded a photo yet
 *   - the image fails to load (network glitch, deleted file)
 *
 * Sizes follow Tailwind's w-* h-* utility scale so callers can override.
 */
import React, { useState } from "react";

const initialsOf = (name) =>
    (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";

export function DoctorAvatar({
    src,
    name,
    size = 36,
    className = "",
    initialsClassName = "bg-indigo-50 border border-indigo-200 text-indigo-700",
    ringClassName = "",
    onClick,
    testid,
}) {
    const [broken, setBroken] = useState(false);
    const showImg = !!src && !broken;
    const style = { width: size, height: size };
    // Initials font sizing scales roughly with the avatar diameter.
    const fontSize = Math.max(10, Math.round(size * 0.38));

    const base = `rounded-full overflow-hidden grid place-items-center font-semibold shrink-0 ${ringClassName}`;
    const Tag = onClick ? "button" : "div";

    if (showImg) {
        return (
            <Tag
                type={onClick ? "button" : undefined}
                onClick={onClick}
                className={`${base} ${className}`}
                style={style}
                data-testid={testid}
            >
                <img
                    src={src}
                    alt={name || "Doctor"}
                    className="w-full h-full object-cover"
                    onError={() => setBroken(true)}
                    draggable={false}
                />
            </Tag>
        );
    }

    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`${base} ${initialsClassName} ${className}`}
            style={{ ...style, fontSize }}
            data-testid={testid}
        >
            {initialsOf(name)}
        </Tag>
    );
}

export default DoctorAvatar;
